import { ChevronDown, Headphones, LogOut, UserRound } from 'lucide-react'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import {
  accountTypeLabel,
  readCachedAccountContext,
} from '@/features/account/model/account-context'
import {
  clearLegacySession,
  getAccountProfile,
  getInitials,
} from '@/features/account/model/account-profile'
import { ProfileAvatar } from '@/features/account/components/profile/ProfileAvatar'
import { redirectToProbeStackLogout } from '@/features/session/services/session-navigation'
import { supportPortalUrl } from '@/config/external-links'

const accountLinks = [
  // { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { label: 'Account settings', to: '/profile', icon: UserRound },
] as const

export function HeaderAccountMenu() {
  const id = useId()
  const { pathname } = useLocation()
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const profile = getAccountProfile()
  const accountType = accountTypeLabel(readCachedAccountContext(), profile.role)

  useLayoutEffect(() => {
    if (!open) return

    function reposition() {
      const anchor = trigger.current?.getBoundingClientRect()
      if (!anchor) return
      const width = Math.min(304, document.documentElement.clientWidth - 24)
      setPosition({
        top: anchor.bottom + 8,
        left: Math.max(
          12,
          Math.min(
            anchor.right - width,
            document.documentElement.clientWidth - width - 12,
          ),
        ),
      })
    }

    function dismissOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      document.getElementById(id)?.hidePopover()
      trigger.current?.focus({ preventScroll: true })
    }

    reposition()
    window.addEventListener('keydown', dismissOnEscape)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('keydown', dismissOnEscape)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [id, open])

  const close = () => document.getElementById(id)?.hidePopover()

  const signOut = () => {
    close()
    clearLegacySession()
    redirectToProbeStackLogout()
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        popoverTarget={id}
        aria-label="Open account menu"
        aria-expanded={open}
        aria-controls={id}
        aria-haspopup="menu"
        className="flex h-10 min-w-0 shrink-0 items-center gap-2 rounded-lg cursor-pointer border border-border-default bg-surface px-1.5 text-primary transition-colors hover:border-border-strong hover:bg-hover"
      >
        <span className="grid size-7 shrink-0 place-items-center rounded-md bg-accent-faint text-[11px] font-bold text-accent">
          {getInitials(profile.name)}
        </span>
        <span className="hidden min-w-0 text-left xl:block">
          <span className="block max-w-36 truncate text-xs leading-4 font-semibold text-primary">
            {profile.name}
          </span>
          <span className="block max-w-36 truncate text-[10px] leading-3 font-medium tracking-wide text-muted uppercase">
            {accountType}
          </span>
        </span>
        <ChevronDown
          className={`hidden size-3.5 shrink-0 text-muted transition-transform xl:block ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {createPortal(
        <div
          id={id}
          popover="auto"
          role="menu"
          aria-label="Account menu"
          onToggle={(event) => setOpen(event.newState === 'open')}
          className="fixed m-0 w-[min(304px,calc(100vw-24px))] overflow-hidden rounded-xl border border-border-strong bg-raised p-0 text-primary shadow-lg"
          style={position}
        >
          <div className="flex items-center gap-3 border-b border-border-default bg-surface/60 p-4">
            <ProfileAvatar
              name={profile.name}
              initials={getInitials(profile.name)}
              emailVerified={null}
              size="3.25rem"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-primary">
                {profile.name}
              </p>
              <p className="truncate text-xs text-muted">{profile.email}</p>
              <p className="mt-1 truncate text-[10px] font-semibold tracking-wider text-secondary uppercase">
                {profile.organization || 'ForgeSphere'} · {accountType}
              </p>
            </div>
          </div>

          <nav className="p-1.5" aria-label="Account destinations">
            {accountLinks.map(({ label, to, icon: Icon }) => {
              const active =
                to === '/profile'
                  ? pathname === to
                  : pathname === to || pathname.startsWith(`${to}/`)
              return (
                <Link
                  key={to}
                  to={to}
                  prefetch="intent"
                  role="menuitem"
                  aria-current={active ? 'page' : undefined}
                  onClick={close}
                  className="flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm text-secondary no-underline transition-colors hover:bg-inset hover:text-primary aria-[current=page]:bg-accent-faint aria-[current=page]:text-accent"
                >
                  <Icon className="size-4" aria-hidden="true" />
                  {label}
                </Link>
              )
            })}
            <a
              href={supportPortalUrl}
              target="_blank"
              rel="noreferrer"
              role="menuitem"
              onClick={close}
              className="flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm text-secondary no-underline transition-colors hover:bg-inset hover:text-primary"
            >
              <Headphones className="size-4" aria-hidden="true" />
              Help and support
            </a>
          </nav>

          <div className="border-t border-border-default p-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={signOut}
              className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-status-failed transition-colors hover:bg-inset"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
